import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Calendar, CheckCircle2, ChevronDown, Edit3, FileText, MessageSquare, PlusCircle, Shield, X } from 'lucide-react';
import type { FeedbackRecord, FeedbackType } from '../services/feedbackService';
import { createFeedback, updateFeedback } from '../services/feedbackService';
import { aikupData } from '../data/aikupData';

type Mode = 'create' | 'edit';

interface FeedbackEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: Mode;
  departmentKey: string;
  currentEmail: string;
  isDany: boolean;
  initial?: FeedbackRecord | null;
  onCreated: (created: FeedbackRecord) => void;
  onUpdated: (updated: FeedbackRecord) => void;
}

const PRIORITY_LABELS: Record<number, string> = {
  1: 'Basse',
  2: 'Moyenne',
  3: 'Haute',
  4: 'Critique',
};

const TYPE_LABELS: Record<FeedbackType, string> = {
  BUG: 'Bug / anomalie',
  CONTENU: 'Contenu',
  SUGGESTION_CONCEPT: 'Suggestion de concept',
  LANGUE: 'Langue / coquille',
  UX: 'UX / expérience',
  BRAINSTORM: 'Brainstorm / idée brute',
  PROCESSUS_METHODOLOGIE: 'Processus / méthodologie',
  AUTRE: 'Autre',
};

const TYPE_OPTIONS: { value: FeedbackType; label: string }[] = (Object.keys(TYPE_LABELS) as FeedbackType[]).map((k) => ({
  value: k,
  label: TYPE_LABELS[k],
}));

function SmallPill({ children }: { children: React.ReactNode }) {
  return <span className="inline-flex items-center rounded-full border border-white/15 bg-white/10 px-2 py-0.5 text-[11px] font-semibold text-white/90">{children}</span>;
}

interface CustomSelectProps<T extends string | number> {
  value: T;
  onChange: (value: T) => void;
  options: { value: T; label: string }[];
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

function CustomSelect<T extends string | number>({
  value,
  onChange,
  options,
  disabled = false,
  placeholder,
  className = '',
}: CustomSelectProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const selectedOption = options.find((opt) => opt.value === value);
  const displayValue = selectedOption ? selectedOption.label : placeholder || '';

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-bz-blue disabled:opacity-60 flex items-center justify-between ${
          isOpen ? 'ring-2 ring-bz-blue' : ''
        }`}
      >
        <span className={selectedOption ? '' : 'text-white/50'}>{displayValue}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <ul className="absolute z-[10002] w-full mt-1 bg-slate-800 border border-white/10 rounded-xl shadow-2xl max-h-60 overflow-y-auto">
          {options.map((option) => (
            <li
              key={String(option.value)}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              className={`px-3 py-2 cursor-pointer text-sm text-white hover:bg-white/10 transition-colors ${
                value === option.value ? 'bg-bz-blue/30 text-white font-semibold' : ''
              }`}
            >
              {option.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function FeedbackEditorModal({
  isOpen,
  onClose,
  mode,
  departmentKey,
  currentEmail,
  isDany,
  initial,
  onCreated,
  onUpdated,
}: FeedbackEditorModalProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Create fields
  const [createContext, setCreateContext] = useState<'GENERAL' | 'DEPARTMENT' | 'CARD'>('CARD');
  const [createCardId, setCreateCardId] = useState<number | ''>('');

  // Shared/edit fields
  const [type, setType] = useState<FeedbackType>('AUTRE');
  const [priority, setPriority] = useState<number>(2);
  const [expectedDate, setExpectedDate] = useState<string>('');
  const [promisedDate, setPromisedDate] = useState<string>('');
  const [comment, setComment] = useState<string>('');
  const [approvedManager, setApprovedManager] = useState<boolean>(false);
  const [approvedCe, setApprovedCe] = useState<boolean>(false);
  const [approvedDany, setApprovedDany] = useState<boolean>(false);

  const cards = useMemo(() => {
    // On affiche toutes les cartes connues pour éviter de “perdre” un id.
    return aikupData
      .slice()
      .map((c) => ({ id: c.id, title: c.title }))
      .sort((a, b) => a.title.localeCompare(b.title, 'fr'));
  }, []);

  const canEditThis = useMemo(() => {
    if (mode !== 'edit' || !initial) return true;
    const owner = (initial.user_email || '').toLowerCase();
    return isDany || owner === currentEmail.toLowerCase();
  }, [mode, initial, isDany, currentEmail]);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setSaving(false);

    if (mode === 'create') {
      setCreateContext('CARD');
      setCreateCardId('');
      setType('AUTRE');
      setPriority(2);
      setExpectedDate('');
      setPromisedDate('');
      setComment('');
      setApprovedManager(true);
      setApprovedCe(false);
      setApprovedDany(false);
      return;
    }

    // mode edit
    if (initial) {
      setType((initial.type as FeedbackType) || 'AUTRE');
      setPriority(Number(initial.priority || 2));
      setExpectedDate(initial.expected_date ? String(initial.expected_date).slice(0, 10) : '');
      setPromisedDate(initial.promised_date ? String(initial.promised_date).slice(0, 10) : '');
      setComment(initial.comment || '');
      setApprovedManager(!!initial.approved_manager);
      setApprovedCe(!!initial.approved_ce);
      setApprovedDany(!!initial.approved_dany);
    }
  }, [isOpen, mode, initial]);

  const title = mode === 'create' ? 'Nouveau commentaire' : 'Éditer commentaire';
  const Icon = mode === 'create' ? PlusCircle : Edit3;

  const handleSubmit = async () => {
    setError(null);
    if (saving) return;

    if (!comment.trim()) {
      setError('Le commentaire est requis.');
      return;
    }

    if (mode === 'create' && createContext === 'CARD' && !createCardId) {
      setError('Choisissez une carte (ou changez la portée).');
      return;
    }

    setSaving(true);
    try {
      if (mode === 'create') {
        const created = await createFeedback({
          context_level: createContext,
          department_key: departmentKey,
          card_id: createContext === 'CARD' ? Number(createCardId) : null,
          type,
          priority,
          expected_date: expectedDate || null,
          comment,
          approved_manager: true,
        });
        onCreated(created);
        onClose();
        return;
      }

      if (!initial?.id) {
        setError('Impossible: commentaire sans identifiant.');
        return;
      }
      if (!canEditThis) {
        setError('Vous ne pouvez modifier que vos propres commentaires (sauf Dany).');
        return;
      }

      const updated = await updateFeedback(initial.id, {
        type,
        priority,
        expected_date: expectedDate || null,
        ...(isDany ? { promised_date: promisedDate || null } : {}),
        comment,
        approved_manager: approvedManager,
        ...(isDany ? { approved_ce: approvedCe, approved_dany: approvedDany } : {}),
        department_key: departmentKey,
        // requis par l’API pour retrouver le contexte
        card_id: initial.context_level === 'CARD' ? initial.card_id ?? null : null,
      });
      onUpdated(updated);
      onClose();
    } catch (e) {
      console.error(e);
      setError(e instanceof Error ? e.message : 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-10000"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.97, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 10 }}
            onClick={(e) => e.stopPropagation()}
            className="fixed inset-0 z-10001 flex items-center justify-center p-4"
          >
            <div className="w-full max-w-3xl overflow-hidden rounded-2xl border border-white/10 bg-linear-to-br from-slate-950 via-slate-900 to-black shadow-2xl">
              {/* Header */}
              <div className="p-5 border-b border-white/10 bg-linear-to-r from-bz-blue/25 via-indigo-600/15 to-purple-600/15">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-11 h-11 rounded-2xl bg-white/10 border border-white/10 flex items-center justify-center">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-extrabold text-white">{title}</h3>
                      <div className="mt-1 flex items-center gap-2 flex-wrap">
                        <SmallPill>{departmentKey}</SmallPill>
                        {mode === 'edit' && initial?.context_level && <SmallPill>{initial.context_level}</SmallPill>}
                        {mode === 'edit' && initial?.card_id != null && <SmallPill>Carte #{initial.card_id}</SmallPill>}
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={onClose}
                    className="p-2 rounded-xl hover:bg-white/10 text-white/80 hover:text-white transition-colors"
                    aria-label="Fermer"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-5 md:p-6 space-y-4">
                {mode === 'create' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-white/80">Portée</label>
                      <CustomSelect
                        value={createContext}
                        onChange={(val) => setCreateContext(val)}
                        options={[
                          { value: 'CARD' as const, label: 'Carte' },
                          { value: 'DEPARTMENT' as const, label: 'Département' },
                          { value: 'GENERAL' as const, label: 'Général' },
                        ]}
                        disabled={saving}
                        className="w-full"
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-white/80">Carte (si portée = Carte)</label>
                      <CustomSelect<number | ''>
                        value={createCardId}
                        onChange={(val) => setCreateCardId(val)}
                        options={[
                          { value: '' as const, label: '— Sélectionner une carte —' },
                          ...cards.map((c) => ({ value: c.id, label: c.title })),
                        ]}
                        disabled={saving || createContext !== 'CARD'}
                        className="w-full"
                      />
                    </div>
                  </div>
                )}

                {mode === 'edit' && initial && (
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                    <div className="flex items-center gap-3 flex-wrap text-sm text-white/85">
                      <span className="inline-flex items-center gap-2">
                        <Shield className="w-4 h-4" />
                        Auteur: <strong>{initial.user_email}</strong>
                      </span>
                      {initial.created_at && (
                        <span className="inline-flex items-center gap-2 text-white/70">
                          <Calendar className="w-4 h-4" />
                          Créé: {new Date(initial.created_at).toLocaleString('fr-CA')}
                        </span>
                      )}
                      {initial.context_level === 'CARD' && initial.card_id != null && (
                        <span className="inline-flex items-center gap-2 text-white/70">
                          <FileText className="w-4 h-4" />
                          Carte: <strong>{aikupData.find((x) => x.id === initial.card_id)?.title || `#${initial.card_id}`}</strong>
                        </span>
                      )}
                    </div>
                    {!canEditThis && (
                      <div className="mt-3 text-sm text-amber-200">
                        Vous pouvez consulter, mais <strong>pas modifier</strong> ce commentaire (seul l’auteur ou Dany).
                      </div>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-white/80">Type</label>
                    <CustomSelect
                      value={type}
                      onChange={(val) => setType(val)}
                      options={TYPE_OPTIONS}
                      disabled={saving || (mode === 'edit' && !canEditThis)}
                      className="w-full"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-white/80">Priorité</label>
                    <CustomSelect
                      value={priority}
                      onChange={(val) => setPriority(val)}
                      options={[1, 2, 3, 4].map((p) => ({
                        value: p,
                        label: `${p} – ${PRIORITY_LABELS[p]}`,
                      }))}
                      disabled={saving || (mode === 'edit' && !canEditThis)}
                      className="w-full"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-white/80">Date souhaitée</label>
                    <input
                      type="date"
                      className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-bz-blue disabled:opacity-60"
                      value={expectedDate}
                      onChange={(e) => setExpectedDate(e.target.value)}
                      disabled={saving || (mode === 'edit' && !canEditThis)}
                    />
                  </div>
                </div>

                {mode === 'edit' && isDany && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-white/80">Date promise (Dany)</label>
                      <input
                        type="date"
                        className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-bz-blue"
                        value={promisedDate}
                        onChange={(e) => setPromisedDate(e.target.value)}
                        disabled={saving}
                      />
                    </div>
                    <div className="flex items-end gap-3">
                      <label className="inline-flex items-center gap-2 text-sm text-white/85">
                        <input type="checkbox" checked={approvedCe} onChange={(e) => setApprovedCe(e.target.checked)} />
                        ✔ CE
                      </label>
                      <label className="inline-flex items-center gap-2 text-sm text-white/85">
                        <input type="checkbox" checked={approvedDany} onChange={(e) => setApprovedDany(e.target.checked)} />
                        ✔ Dany
                      </label>
                    </div>
                  </div>
                )}

                {mode === 'edit' && (
                  <div className="flex items-center gap-3 flex-wrap">
                    <label className="inline-flex items-center gap-2 text-sm text-white/85">
                      <input
                        type="checkbox"
                        checked={approvedManager}
                        onChange={(e) => setApprovedManager(e.target.checked)}
                        disabled={saving || !canEditThis}
                      />
                      ✔ Gestionnaire
                    </label>
                  </div>
                )}

                <div className="space-y-1">
                  <label className="text-xs font-semibold text-white/80">Commentaire</label>
                  <div className="relative">
                    <MessageSquare className="w-4 h-4 text-white/35 absolute left-3 top-3" />
                    <textarea
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      className="w-full min-h-[140px] rounded-2xl border border-white/10 bg-white/5 pl-9 pr-3 py-2.5 text-sm text-white placeholder:text-white/35 focus:outline-none focus:ring-2 focus:ring-bz-blue disabled:opacity-60"
                      placeholder="Décrire le changement, le contexte, l’impact…"
                      disabled={saving || (mode === 'edit' && !canEditThis)}
                    />
                  </div>
                </div>

                {error && (
                  <div className="rounded-xl border border-red-400/25 bg-red-500/10 p-3 text-sm text-red-100">
                    {error}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-5 border-t border-white/10 bg-black/30 flex items-center justify-between gap-3">
                <button
                  onClick={onClose}
                  className="rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold text-white/85 hover:bg-white/10 transition-colors"
                  disabled={saving}
                >
                  Annuler
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={saving || (mode === 'edit' && !canEditThis)}
                  className="inline-flex items-center gap-2 rounded-xl bg-white px-5 py-2 text-sm font-extrabold text-slate-900 hover:bg-slate-100 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <span>Enregistrement…</span>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4" />
                      <span>{mode === 'create' ? 'Créer' : 'Enregistrer'}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

